// Landing page when the operator has no active project — usually because
// they just signed up, or their last project was archived. Lists every
// project across every workspace they're a member of and lets them pick
// one with a single click. After picking, they're sent to /clients.
//
// Also reachable from the topbar at /projects, so an operator with an
// active project can come here to switch context manually.
//
// Includes an "Archived" section showing soft-deleted workspaces /
// projects with restore buttons (within their 14-day grace window).
// Workspaces only restorable by OWNER; projects by OWNER or ADMIN.

import {
  Box,
  Button,
  Heading,
  HStack,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiFolder, FiRotateCcw } from "react-icons/fi";
import {
  useMyWorkspaces,
  useRestoreProject,
  useRestoreWorkspace,
  useSetActiveProject,
} from "@/hooks/useWorkspaces";
import { useMe } from "@/hooks/useAuth";

function showToast(kind: "success" | "error", text: string) {
  if (typeof document === "undefined") return;
  const el = document.createElement("div");
  el.textContent = text;
  el.style.cssText = `
    position: fixed; top: 16px; right: 16px; z-index: 9999;
    padding: 10px 14px; border-radius: 8px; font: 500 14px system-ui;
    color: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.25);
    background: ${kind === "success" ? "#16a34a" : "#dc2626"};
  `;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2500);
}

export default function ProjectsPicker() {
  const { data: me } = useMe();
  // Pull archived ones too so we can render a Restore section below.
  const { data, isLoading } = useMyWorkspaces({ includeArchived: true });
  const setActive = useSetActiveProject();
  const restoreWorkspace = useRestoreWorkspace();
  const restoreProject = useRestoreProject();
  const navigate = useNavigate();

  // After a successful pick, navigate to the default per-project page.
  // useEffect rather than .onSuccess so the navigation runs after
  // TanStack Query has refetched me + invalidated everything.
  useEffect(() => {
    if (
      setActive.isSuccess &&
      me?.lastActiveProjectId === setActive.data?.lastActiveProjectId
    ) {
      navigate("/clients");
    }
  }, [setActive.isSuccess, setActive.data, me?.lastActiveProjectId, navigate]);

  if (isLoading || !data) {
    return (
      <HStack p={8}>
        <Spinner size="md" />
        <Text color="fg.muted">Loading workspaces…</Text>
      </HStack>
    );
  }

  const liveWorkspaces = data.workspaces.filter((w) => !w.archivedAt);
  const archivedWorkspaces = data.workspaces.filter((w) => w.archivedAt);
  // Active projects in still-live workspaces. Archived projects in live
  // workspaces are shown in the Restore section below.
  const totalLiveProjects = liveWorkspaces.reduce(
    (sum, w) => sum + w.projects.filter((p) => !p.archivedAt).length,
    0,
  );
  const archivedProjects = liveWorkspaces.flatMap((w) =>
    w.projects
      .filter((p) => p.archivedAt)
      .map((p) => ({ workspace: w, project: p })),
  );

  return (
    <Stack h="100%" minH={0} overflowY="auto" gap={8} maxW="3xl" mx="auto" py={6}>
      <Box>
        <Heading size="xl">Pick a project</Heading>
        <Text color="fg.muted" fontSize="sm" mt={1}>
          You can switch between projects at any time from the dropdown in
          the top bar.
        </Text>
      </Box>

      {totalLiveProjects === 0 ? (
        <Box>
          <Text color="fg.muted">
            You don't have any active projects. If you expected to see one,
            check with your workspace owner that you've been added, or
            restore an archived project / workspace from the sections below.
          </Text>
        </Box>
      ) : (
        liveWorkspaces.map((ws) => {
          const activeProjects = ws.projects.filter((p) => !p.archivedAt);
          if (activeProjects.length === 0) return null;
          return (
            <Stack key={ws.id} gap={3}>
              <HStack>
                <Heading size="md">{ws.name}</Heading>
                <Text fontSize="xs" color="fg.muted" textTransform="lowercase">
                  ({ws.role})
                </Text>
              </HStack>
              <Stack gap={2}>
                {activeProjects.map((p) => {
                  const isActive = me?.lastActiveProjectId === p.id;
                  return (
                    <Button
                      key={p.id}
                      variant="outline"
                      justifyContent="space-between"
                      h="auto"
                      py={3}
                      px={4}
                      loading={
                        setActive.isPending && setActive.variables === p.id
                      }
                      onClick={() => setActive.mutate(p.id)}
                    >
                      <HStack gap={3}>
                        <FiFolder />
                        <Text fontWeight="medium">{p.name}</Text>
                        {isActive ? (
                          <Text fontSize="xs" color="fg.muted">
                            (currently active)
                          </Text>
                        ) : null}
                      </HStack>
                      <FiArrowRight />
                    </Button>
                  );
                })}
              </Stack>
            </Stack>
          );
        })
      )}

      {archivedProjects.length > 0 || archivedWorkspaces.length > 0 ? (
        <Stack gap={3} pt={6} borderTopWidth="1px" borderColor="border.muted">
          <Heading size="md" color="fg.muted">
            Archived
          </Heading>
          <Text fontSize="sm" color="fg.muted">
            Soft-deleted within the last 14 days. Restore to make
            available again; after the grace window they're permanently
            removed by the nightly cleanup.
          </Text>

          {archivedProjects.map(({ workspace, project }) => {
            const canRestore =
              workspace.role === "OWNER" || workspace.role === "ADMIN";
            return (
              <HStack key={project.id} gap={3} justify="space-between">
                <Stack gap={0}>
                  <Text>
                    {project.name}{" "}
                    <Text as="span" fontSize="xs" color="fg.muted">
                      project · {workspace.name}
                    </Text>
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    Archived {new Date(project.archivedAt!).toLocaleDateString()}
                  </Text>
                </Stack>
                {canRestore ? (
                  <Button
                    size="sm"
                    variant="outline"
                    loading={restoreProject.isPending}
                    onClick={() =>
                      restoreProject.mutate(
                        {
                          workspaceId: workspace.id,
                          projectId: project.id,
                        },
                        {
                          onSuccess: () => showToast("success", "Project restored"),
                          onError: (err) =>
                            showToast(
                              "error",
                              err instanceof Error
                                ? err.message
                                : "Failed to restore",
                            ),
                        },
                      )
                    }
                  >
                    <FiRotateCcw />
                    Restore
                  </Button>
                ) : null}
              </HStack>
            );
          })}

          {archivedWorkspaces.map((ws) => (
            <HStack key={ws.id} gap={3} justify="space-between">
              <Stack gap={0}>
                <Text>
                  {ws.name}{" "}
                  <Text as="span" fontSize="xs" color="fg.muted">
                    workspace
                  </Text>
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  Archived {new Date(ws.archivedAt!).toLocaleDateString()}
                </Text>
              </Stack>
              {ws.role === "OWNER" ? (
                <Button
                  size="sm"
                  variant="outline"
                  loading={restoreWorkspace.isPending}
                  onClick={() =>
                    restoreWorkspace.mutate(ws.id, {
                      onSuccess: () => showToast("success", "Workspace restored"),
                      onError: (err) =>
                        showToast(
                          "error",
                          err instanceof Error
                            ? err.message
                            : "Failed to restore",
                        ),
                    })
                  }
                >
                  <FiRotateCcw />
                  Restore
                </Button>
              ) : null}
            </HStack>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}
